import {
  AttachmentPreviewList,
  useMessageComposer,
  TextareaComposer,
  useMessageComposerHasSendableData,
  useMessageInputContext,
  useChannelStateContext,
  useStateStore,
} from 'stream-chat-react'
import { createStyles } from '@codeleap/styles'
import { ActionIcon, GIFPickerModal, CreatePollModal, ActivityIndicator } from '@/components'
import { AttachmentModal, AudioRecorder } from './components'
import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useAutoHeightResize } from '@/hooks'
import { theme } from '@/config'
import { ChatToast } from '../ChatToast'
import { useModal } from '@codeleap/hooks'
import { Chat } from '@/config/packages'
import { t } from '@lingui/core/macro'
import { useNetworkStatus } from '@/hooks'
import { useAttachmentStatus } from './hooks/useAttachmentStatus'
import { useAttachmentOfflineStyle } from './hooks/useAttachmentOfflineStyle'
import { useAttachmentListObserver } from './hooks/useAttachmentListObserver'

export type ChatInputProps = {
  threadList?: boolean
}

const MAX_FILE_SIZE = 100 * 1024 * 1024

const MOBILE_FRIENDLY_MIME_TYPES = [
  'audio/mp4',
  'audio/aac',
  'audio/m4a',
  'audio/x-m4a',
  'audio/wav',
  'audio/x-wav',
]

const getAudioFileExtension = (mimeType: string) => {
  if (mimeType.includes('mp4') || mimeType.includes('aac') || mimeType.includes('m4a')) return 'm4a'
  if (mimeType.includes('ogg')) return 'ogg'
  if (mimeType.includes('wav')) return 'wav'
  if (mimeType.includes('webm')) return 'webm'

  return 'm4a'
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const interleave = (channels: Float32Array[]) => {
  const length = channels[0]?.length || 0
  const result = new Float32Array(length * channels.length)

  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < channels.length; channel++) {
      result[i * channels.length + channel] = channels[channel]?.[i] ?? 0
    }
  }

  return result
}

const audioBufferToWaveBlob = (audioBuffer: AudioBuffer) => {
  const numChannels = audioBuffer.numberOfChannels || 1
  const sampleRate = audioBuffer.sampleRate || 44100
  const channels = Array.from({ length: numChannels }, (_, index) => audioBuffer.getChannelData(index))
  const interleaved = interleave(channels)
  const bitsPerSample = 16
  const blockAlign = (numChannels * bitsPerSample) / 8
  const byteRate = sampleRate * blockAlign
  const wavBuffer = new ArrayBuffer(44 + interleaved.length * 2)
  const view = new DataView(wavBuffer)

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }

  let offset = 0
  writeString(offset, 'RIFF')
  offset += 4
  view.setUint32(offset, 36 + interleaved.length * 2, true)
  offset += 4
  writeString(offset, 'WAVE')
  offset += 4
  writeString(offset, 'fmt ')
  offset += 4
  view.setUint32(offset, 16, true)
  offset += 4
  view.setUint16(offset, 1, true)
  offset += 2
  view.setUint16(offset, numChannels, true)
  offset += 2
  view.setUint32(offset, sampleRate, true)
  offset += 4
  view.setUint32(offset, byteRate, true)
  offset += 4
  view.setUint16(offset, blockAlign, true)
  offset += 2
  view.setUint16(offset, bitsPerSample, true)
  offset += 2
  writeString(offset, 'data')
  offset += 4
  view.setUint32(offset, interleaved.length * 2, true)
  offset += 4

  for (let i = 0; i < interleaved.length; i++, offset += 2) {
    const sample = clamp(interleaved[i], -1, 1)
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
  }

  return new Blob([view], { type: 'audio/wav' })
}

const extractWaveformData = (audioBuffer: AudioBuffer, samples = 100) => {
  const channelData = audioBuffer.getChannelData(0)
  const blockSize = Math.max(1, Math.floor(channelData.length / samples))
  const waveform: number[] = []

  for (let i = 0; i < samples; i++) {
    const start = i * blockSize
    const end = Math.min(start + blockSize, channelData.length)
    let sum = 0

    for (let j = start; j < end; j++) {
      sum += Math.abs(channelData[j])
    }

    const avg = sum / Math.max(1, end - start)
    waveform.push(clamp(avg, 0, 1))
  }

  const maxValue = Math.max(...waveform, 0)

  if (maxValue <= 0) return waveform

  return waveform.map(value => clamp(Math.pow(value / maxValue, 0.7), 0, 1))
}

let sharedAudioContext: AudioContext | null = null

const getAudioContext = () => {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return null
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    sharedAudioContext = new AudioContext()
  }
  return sharedAudioContext
}

const cleanupAudioContext = () => {
  if (sharedAudioContext && sharedAudioContext.state !== 'closed') {
    sharedAudioContext.close()
    sharedAudioContext = null
  }
}

const normalizeAudioBlobForMobile = async (blob: Blob) => {
  if (!blob || blob.size === 0) {
    return {
      blob,
      mimeType: 'audio/mp4',
      waveformData: undefined,
      duration: undefined,
    }
  }

  const mimeType = blob.type || 'audio/mp4'
  const audioContext = getAudioContext()

  if (!audioContext) {
    return {
      blob,
      mimeType,
      waveformData: undefined,
      duration: undefined,
    }
  }

  try {
    if (audioContext.state === 'suspended') {
      await audioContext.resume()
    }

    await new Promise(requestAnimationFrame)
    const arrayBuffer = await blob.arrayBuffer()

    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      throw new Error('Empty or invalid audio data')
    }

    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0))
    const waveformData = extractWaveformData(audioBuffer)
    const duration = audioBuffer.duration

    if (MOBILE_FRIENDLY_MIME_TYPES.some(type => mimeType.includes(type))) {
      return { blob, mimeType, waveformData, duration }
    }

    const wavBlob = audioBufferToWaveBlob(audioBuffer)

    return {
      blob: wavBlob,
      mimeType: 'audio/wav',
      waveformData,
      duration,
    }
  } catch (err) {
    console.warn('Failed to normalize audio for mobile playback', err)

    return {
      blob,
      mimeType,
      waveformData: undefined,
      duration: undefined,
    }
  }
}

function textComposerStateSelector(state) {
  return {
    text: state.text,
    mentionedUsers: state.mentionedUsers,
  }
}

export const ChatInput = ({ threadList }: ChatInputProps) => {
  const { handleSubmit } = useMessageInputContext()
  const { channel } = useChannelStateContext()
  const gifModal = useModal()
  const [isRecording, setIsRecording] = useState(false)
  const [isSendingAudio, setIsSendingAudio] = useState(false)
  const pollModal = useModal()
  const [hasExcessFileSize, setHasExcessFileSize] = useState(false)

  useEffect(() => {
    return () => {
      cleanupAudioContext()
    }
  }, [])

  const { attachmentManager, textComposer } = useMessageComposer()
  const { mentionedUsers, text } = useStateStore(textComposer.state, textComposerStateSelector)
  const { maxMentionsReached } = Chat.Hooks.useMentionsLimit(mentionedUsers, text)
  const hasSendableData = useMessageComposerHasSendableData()
  const isOnline = useNetworkStatus()

  const handleReloadAttachmentRef = useRef<((id: string) => Promise<void>) | null>(null)

  const {
    uploadState,
    uploadActions,
    updateAttachmentStyles,
    shouldShowFailedMessage,
    failedUploadsRef,
  } = useAttachmentStatus(attachmentManager?.attachments, isOnline, (id: string) => {
    handleReloadAttachmentRef.current?.(id)
  })

  const attachmentOfflineStyle = useAttachmentOfflineStyle((id: string) => {
    handleReloadAttachmentRef.current?.(id)
  })

  handleReloadAttachmentRef.current = useCallback(async (attachmentId: string) => {
    const file = failedUploadsRef.current.get(attachmentId)
    if (!file || !navigator.onLine) return

    uploadActions.removeFailed(attachmentId)

    const attachmentList = document.querySelector('.str-chat__attachment-preview-list')
    if (attachmentList && attachmentManager?.attachments) {
      const attachmentIndex = attachmentManager.attachments.findIndex((att: any) => {
        const attId = att.id || `attachment-${attachmentManager.attachments.indexOf(att)}`
        return attId === attachmentId
      })
      if (attachmentIndex >= 0) {
        attachmentOfflineStyle.removeOfflineStyle(attachmentId, attachmentIndex)
      }
    }

    try {
      uploadActions.addUploadingFiles(new Map([[attachmentId, file]]))
      await attachmentManager?.uploadFiles?.([file])

      setTimeout(() => {
        const attachment = attachmentManager?.attachments?.find((att: any) => {
          const attId = att.id || `attachment-${attachmentManager.attachments.indexOf(att)}`
          return attId === attachmentId
        })
        const hasUrl = attachment?.asset_url || attachment?.image_url || attachment?.thumb_url || (attachment as any)?.file_url

        if (!hasUrl) {
          uploadActions.markAsFailed(attachmentId, file)
          if (attachmentList && attachmentManager?.attachments) {
            const attachmentIndex = attachmentManager.attachments.findIndex((att: any) => {
              const attId = att.id || `attachment-${attachmentManager.attachments.indexOf(att)}`
              return attId === attachmentId
            })
            if (attachmentIndex >= 0) {
              attachmentOfflineStyle.applyOfflineStyle(attachment as any, attachmentIndex, true)
            }
          }
        } else {
          uploadActions.removeUploadingFile(attachmentId)
        }
      }, 2000)
    } catch (error) {
      console.error('Error retrying upload:', error)
      uploadActions.markAsFailed(attachmentId, file)
      const attachmentList = document.querySelector('.str-chat__attachment-preview-list')
      if (attachmentList && attachmentManager?.attachments) {
        const attachmentIndex = attachmentManager.attachments.findIndex((att: any) => {
          const attId = att.id || `attachment-${attachmentManager.attachments.indexOf(att)}`
          return attId === attachmentId
        })
        if (attachmentIndex >= 0) {
          attachmentOfflineStyle.applyOfflineStyle(attachmentManager.attachments[attachmentIndex] as any, attachmentIndex, true)
        }
      }
    }
  }, [attachmentManager, uploadActions, attachmentOfflineStyle, failedUploadsRef])

  useAttachmentListObserver(updateAttachmentStyles)

  useEffect(() => {
    if (!isOnline && uploadState.uploadingFileIds.size > 0) {
      uploadActions.markAllAsFailed(uploadState.uploadingFileIds)
      setTimeout(updateAttachmentStyles, 100)
    }
  }, [isOnline, uploadState.uploadingFileIds.size, uploadActions, updateAttachmentStyles])

  attachmentManager?.uploadsInProgressCount

  const attachmentsCount = attachmentManager?.attachments?.length || 0
  const hasExcessAttachments = attachmentsCount >= 10
  const isUploadingAttachments = attachmentManager?.uploadsInProgressCount > 0

  const hasOversizedFiles = useMemo(() => {
    return (
      hasExcessFileSize ||
      attachmentManager?.attachments?.some(
        (attachment: any) => attachment?.file_size && attachment.file_size > MAX_FILE_SIZE,
      ) ||
      false
    )
  }, [hasExcessFileSize, attachmentManager?.attachments])

  const handleAttachments = useCallback(async () => {
    const selectedFiles = await AttachmentModal.request({
      onGIFPress: gifModal?.open,
      onPollPress: pollModal?.open,
      showPollOption: !threadList,
    })

    if (selectedFiles) {
      const validFiles = selectedFiles.filter(f => f.file.size <= MAX_FILE_SIZE)
      const rejectedFiles = selectedFiles.filter(f => f.file.size > MAX_FILE_SIZE)

      setHasExcessFileSize(rejectedFiles.length > 0)

      if (validFiles.length > 0) {
        const fileMap = new Map<string, File>()
        validFiles.forEach((fileData, index) => {
          const fileId = `upload-${Date.now()}-${index}-${fileData.file.name}`
          fileMap.set(fileId, fileData.file)
        })

        uploadActions.addUploadingFiles(fileMap)

        try {
          if (!navigator.onLine) {
            uploadActions.markAllAsFailed(fileMap)
            return
          }

          await attachmentManager?.uploadFiles?.(validFiles.map(f => f.file))

          setTimeout(() => {
            fileMap.forEach((_, id) => {
              uploadActions.removeUploadingFile(id)
            })
          }, 2000)
        } catch (error) {
          uploadActions.markAllAsFailed(fileMap)
          console.error('Error uploading files:', error)
        }
      }
    }
  }, [gifModal, pollModal, threadList, uploadActions, attachmentManager])

  const handleStartRecording = () => {
    setIsRecording(true)
  }

  const handleRecordingComplete = async (audioBlob: Blob, duration: number) => {
    setIsRecording(false)

    if (!audioBlob || audioBlob.size === 0) {

      Chat.Store.setToastMessage('Recording failed. Please try again.')
      return
    }

    setIsSendingAudio(true)

    try {
      await new Promise(resolve => setTimeout(resolve, 100))

      const {
        blob: normalizedBlob,
        mimeType,
        waveformData,
        duration: normalizedDuration,
      } = await normalizeAudioBlobForMobile(audioBlob)

      if (!normalizedBlob || normalizedBlob.size === 0) {
        console.error('Audio normalization failed:', { originalSize: audioBlob.size, normalizedSize: normalizedBlob?.size })
        Chat.Store.setToastMessage('Failed to process audio. Please try again.')
        return
      }

      const extension = getAudioFileExtension(mimeType)

      const audioFile = new File([normalizedBlob], `voice-message-${Date.now()}.${extension}`, {
        type: mimeType,
      })

      const response = await channel.sendFile(audioFile)

      await Chat.Services.sendAudio(
        channel,
        {
          type: 'voiceRecording',
          asset_url: response.file,
          file_size: audioFile.size,
          mime_type: audioFile.type,
          duration: normalizedDuration ?? duration,
          waveform_data: waveformData?.length ? waveformData : undefined,
        })
    } catch (error) {
      console.error('Error sending voice message:', error)
      Chat.Store.setToastMessage('Failed to send voice message. Please try again.')
    } finally {
      setIsSendingAudio(false)
    }
  }

  const handleRecordingCancel = () => {
    setIsRecording(false)
  }

  return (
    <div style={threadList ? { ...styles.wrapper, ...styles.threadWrapper } : styles.wrapper} className='str-chat__message-input'>
      <ChatToast />

      {isRecording ? (
        <div style={styles.innerWrapper} className='str-chat__message-input-inner'>
          <AudioRecorder
            onComplete={handleRecordingComplete}
            onCancel={handleRecordingCancel}
          />
        </div>
      ) : (
        <div style={styles.innerWrapper} className='str-chat__message-input-inner'>
          <ActionIcon
            name='plus'
            onPress={handleAttachments}
            style={['iconSize:2', 'regularSecondary', styles.actionIcon]}
          />

          <div style={styles.inputArea} className='str-chat__message-textarea-container get_stream-input'>
            <AttachmentPreviewList />
            {hasExcessAttachments && (
              <div style={styles.warningText}>
                {t`You can attach up to 10 files per message`}
              </div>
            )}
            {hasOversizedFiles && (
              <div style={styles.warningText}>
                {t`File size cannot exceed 100 MB`}
              </div>
            )}
            {shouldShowFailedMessage && (
              <div style={styles.warningText}>
                {t`Upload failed. Check your connection and try again`}
              </div>
            )}
            <Textarea />
          </div>

          {(isSendingAudio || isUploadingAttachments) ? (
            <ActivityIndicator style={styles.sendingLoader} />
          ) : hasSendableData ? (
            !maxMentionsReached ? (
              <ActionIcon
                name='arrow-up'
                onPress={handleSubmit}
                style={['iconSize:2', {
                  touchableWrapper: [styles.actionIcon, { opacity: hasSendableData ? 1 : 0 }],
                }]}
              />
            ) : null
          ) : (
            <div className='microphone-icon-wrapper'>
              <ActionIcon
                name='microphone-01'
                onPress={handleStartRecording}
                style={['iconSize:2', styles.actionMicrophoneIcon]}
                className='microphone-icon'
              />
            </div>
          )}
        </div>
      )}
      <GIFPickerModal {...gifModal} />
      <CreatePollModal {...pollModal} />
    </div>
  )
}

const Textarea = () => {
  const textareaRef = useRef<HTMLDivElement>(null)
  useAutoHeightResize(textareaRef, {
    maxHeight: 180,
    className: '.str-chat__textarea__textarea',
    padding: theme.spacing.value(2),
  })

  return (
    <div
      ref={textareaRef}
      style={styles.textareaContainer}
      className='str-chat__message-textarea-with-emoji-picker'
    >
      <TextareaComposer />
    </div>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    padding: theme.value(3),
    position: 'relative',
    flexShrink: 0,
    maxHeight: '50vh',

    [theme.media.down('mobile')]: {
      padding: theme.value(2),
      paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
      maxHeight: '40vh',
    },
  },
  threadWrapper: {
    padding: theme.value(3),
    position: 'relative',
    marginTop: 'auto',
    flexShrink: 0,
    maxHeight: '50vh',
    ...theme.border({
      color: '#f6f6f6',
      directions: ['top'],
      width: 2,
    }),

    [theme.media.down('mobile')]: {
      padding: theme.value(2),
      paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
      maxHeight: '40vh',
    },
  },
  innerWrapper: {
    ...theme.presets.row,
    ...theme.presets.alignEnd,
    ...theme.presets.justifyEnd,
  },
  inputArea: {
    ...theme.presets.fullHeight,
    flex: 1,
    minWidth: 0,
  },
  textareaContainer: {
    '.str-chat__textarea__textarea': {
      minHeight: '20px !important',
      maxHeight: '200px !important',
      height: 'auto !important',
      resize: 'none !important',
      overflow: 'hidden !important',
      lineHeight: '1.4 !important',
      wordWrap: 'break-word !important',
      whiteSpace: 'pre-wrap !important',
      padding: '4px 8px !important',
    },
    '.str-chat__textarea__textarea:focus': {
      overflow: 'hidden !important',
    },
    '.str-chat__textarea__textarea:not(:empty)': {
      overflow: 'hidden auto !important',
    },
  },
  actionIcon: {
    width: theme.size.largeish,
    height: theme.size.largeish,
    minWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxWidth: theme.size.largeish,
    maxHeight: theme.size.largeish,
    ...theme.spacing.padding(0),
  },
  actionMicrophoneIcon: {
    width: theme.size.largeish,
    height: theme.size.largeish,
    minWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxWidth: theme.size.largeish,
    maxHeight: theme.size.largeish,
    ...theme.spacing.padding(0),

    touchableWrapper: {
      backgroundColor: 'transparent',
      border: 'none',
      width: 40,
      height: 40,
      borderRadius: theme.radius.infinite,
      transition: 'background-color 0.2s ease, color 0.2s ease',

      '&:hover': {
        backgroundColor: theme.baseColors.primarySolid500,
      },

      '&:hover svg': {
        color: `${theme.baseColors.neutralSolid0} !important`,
      },

      '&:hover svg path, &:hover path': {
        stroke: `${theme.baseColors.neutralSolid0} !important`,
        fill: `${theme.baseColors.neutralSolid0} !important`,
      },

      '&:hover *': {
        color: `${theme.baseColors.neutralSolid0} !important`,
      },
    },
    icon: {
      color: theme.baseColors.primarySolid500,
      transition: 'color 0.2s ease, stroke 0.2s ease, fill 0.2s ease',
    },
  },
  sendingLoader: {
    width: theme.size.largeish,
    height: theme.size.largeish,
    minWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxWidth: theme.size.largeish,
    maxHeight: theme.size.largeish,
    ...theme.presets.center,
  },
  warningText: {
    fontSize: 12,
    color: theme.baseColors.redSolid500,
    ...theme.spacing.marginTop(0.5),
    ...theme.spacing.marginBottom(0.5),
    ...theme.spacing.paddingLeft(1),
  },
}))
